import type React from 'react'
import { Sun, Moon } from '@phosphor-icons/react'
import styles from './TimetableRow.module.css'
import { StationDetailsCopy, type StationDetailsCopySet } from '../copy'

// TODO(@mammoth/design-kit): add `sun` / `moon` to the icon registry
// (Phosphor names — already a runtime dep) so this file can import
// `Icon` like the other rows. Direct imports here mirror iOS
// `sun.max.fill` / `moon.fill`; until the registry catches up the
// theming + a11y wrappers from <Icon> are not available, so we tint
// via the existing `.labelIcon` rule and mark the glyphs aria-hidden
// directly on the Phosphor component.

export interface TimetableDirection {
  headSign: string
  weekdays: { firstTrain: string; lastTrain: string }
  saturdays: { firstTrain: string; lastTrain: string }
  sundays: { firstTrain: string; lastTrain: string }
}

export interface TimetableRowProps {
  timetable: { directions: ReadonlyArray<TimetableDirection> }
  /** Localised copy (column headers + a11y). @defaultValue {@link StationDetailsCopy} */
  copy?: StationDetailsCopySet
}

/**
 * Pixel-parity with iOS
 * `MetroModules/Sources/StationDetails/Views/Rows/TimetableRow.swift`.
 *
 * Layout: per direction, a head-sign label and a 4-column grid (spacer
 * column for the row label + Mon-Fri + Sat + Sun). Two body rows: First
 * (sun glyph) and Last (moon glyph). Times use tabular-num digits to
 * mirror iOS `.monospacedDigit()`.
 */
export function TimetableRow({
  timetable,
  copy = StationDetailsCopy,
}: TimetableRowProps): React.ReactElement | null {
  if (timetable.directions.length === 0) return null
  return (
    <div data-testid="station-details-timetable" className={styles.root}>
      {timetable.directions.map((d, index) => (
        // Two directions can end at the same station (a loop, a branch).
        <section key={`${index}:${d.headSign}`} className={styles.direction}>
          <h3 className={styles.headSign}>{d.headSign}</h3>
          <div className={styles.grid} role="table">
            {/* Header row */}
            <span className={`${styles.headerCell} ${styles.firstColumn}`} aria-hidden="true" />
            <span className={styles.headerCell} aria-label={copy.TimetableWeekdaysAria}>
              {copy.TimetableWeekdays}
            </span>
            <span className={styles.headerCell} aria-label={copy.TimetableSaturdaysAria}>
              {copy.TimetableSaturdays}
            </span>
            <span className={styles.headerCell} aria-label={copy.TimetableSundaysAria}>
              {copy.TimetableSundays}
            </span>

            {/* First train row */}
            <span className={styles.labelCell}>
              <span className={styles.labelIcon}>
                <Sun size={12} weight="fill" aria-hidden="true" />
              </span>
              {copy.TimetableFirst}
            </span>
            <span className={styles.timeCell}>{d.weekdays.firstTrain}</span>
            <span className={styles.timeCell}>{d.saturdays.firstTrain}</span>
            <span className={styles.timeCell}>{d.sundays.firstTrain}</span>

            {/* Last train row */}
            <span className={styles.labelCell}>
              <span className={styles.labelIcon}>
                <Moon size={12} weight="fill" aria-hidden="true" />
              </span>
              {copy.TimetableLast}
            </span>
            <span className={styles.timeCell}>{d.weekdays.lastTrain}</span>
            <span className={styles.timeCell}>{d.saturdays.lastTrain}</span>
            <span className={styles.timeCell}>{d.sundays.lastTrain}</span>
          </div>
        </section>
      ))}
    </div>
  )
}
